/**
 * Notification subsystem hooks — TanStack Query wrappers over
 * services/notifications.ts.
 *
 * - `useNotifications` reads the mute-filtered bell feed.
 * - `useNotificationPreferences` reads the per-event mute state (backs
 *   the Settings -> Notifications toggles).
 * - `useUpdateNotificationPreferences` writes the full mute set; on
 *   success it primes the preferences cache + invalidates the feed (a
 *   newly-muted event type drops from the feed immediately).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearAllNotifications,
  fetchNotificationPreferences,
  fetchNotifications,
  putNotificationPreferences,
} from "@/services/notifications";
import type {
  NotificationFeedResponse,
  NotificationPreferencesResponse,
  NotificationPreferencesUpdateRequest,
  NotificationsClearAllResponse,
} from "@/types";

const NOTIFICATIONS_FEED_KEY = "notifications-feed";
const NOTIFICATION_PREFERENCES_KEY = "notification-preferences";

export function useNotifications() {
  return useQuery<NotificationFeedResponse>({
    queryKey: [NOTIFICATIONS_FEED_KEY],
    queryFn: () => fetchNotifications({ limit: 50 }),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

export function useNotificationPreferences() {
  return useQuery<NotificationPreferencesResponse>({
    queryKey: [NOTIFICATION_PREFERENCES_KEY],
    queryFn: () => fetchNotificationPreferences(),
    staleTime: 30_000,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation<
    NotificationPreferencesResponse,
    Error,
    NotificationPreferencesUpdateRequest
  >({
    mutationFn: (payload) => putNotificationPreferences(payload),
    onSuccess: (data) => {
      qc.setQueryData([NOTIFICATION_PREFERENCES_KEY], data);
      qc.invalidateQueries({ queryKey: [NOTIFICATIONS_FEED_KEY] });
    },
  });
}

/**
 * "Clear all" — dismiss every currently-shown notification (§ 3a.2; sub-B3).
 * On success the feed is invalidated so the now-cleared items drop from both
 * the dashboard section and the bell.
 */
export function useClearAllNotifications() {
  const qc = useQueryClient();
  return useMutation<NotificationsClearAllResponse, Error, void>({
    mutationFn: () => clearAllNotifications(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [NOTIFICATIONS_FEED_KEY] });
    },
  });
}
